import React, { useCallback, useEffect, useRef } from 'react';
import * as Styled from './VHLogsArea.styled';
import ChatLogItem from '../../molecules/ChatLogItem/ChatLogItem';
import { useDispatch, useSelector } from 'react-redux';
import {
  ChatStates,
  scoreOverviewVisibleSelector,
  selectAllChats,
  selectChatState,
  selectScrollToEndChatLog,
  setScoreOverviewVisible,
  setScrollToEndChatLog,
} from '../../../slices/chat/ChatSlice';
import IconLabelButton from '../../molecules/IconLabelButton/IconLabelButton';
import LegacyScoreOverview from '../../molecules/ScoreOverview/LegacyScoreOverview';
import CardModal from '../../molecules/CardModal/CardModal';
import { usePrevious } from '../../../utility/StateHooks';
import FinishSessionButton from '../../FinishSessionButton/FinishSessionButton';
import {
  courseIDSelector,
  nextUnitSelector,
} from '../../../slices/VirtualHumanSlice';
import UnitCompleteNext from '../../UnitCompleteNext/UnitCompleteNext';
import MicrophoneStreamProvider from '../../../../../core/src/components/MicrophoneStreamProvider/MicrophoneStreamProvider';
import { useFeature } from '../../../../../core/src/components/LaunchDarkly';
import { showableScoreOverviewSelector } from '@vh/slices/vh-player/VHPlayerSliceExtraSelectors';

const VHLogsArea = (props) => {
  const chats = useSelector(selectAllChats);
  const chatLoadingStatus = useSelector((state) => state.chat.status);
  const logsRef = useRef(null);
  const dispatch = useDispatch();
  const chatState = useSelector(selectChatState);

  // scroll to end when chats are updated
  useEffect(() => {
    if (logsRef && logsRef.current) {
      logsRef.current.scrollTo(0, logsRef.current.scrollHeight);
    }
  }, [chats]);

  // use scroll to end chat log to scroll to end
  // (allows other components to notify that e.g. input UI has changed -  where we can't
  // just rely on chats list changing to indicate we need a scroll to end update)
  const scrollToEndChatLog = useSelector(selectScrollToEndChatLog);
  const prevScrollToEndChatLog = usePrevious(scrollToEndChatLog);
  useEffect(() => {
    if (scrollToEndChatLog && !prevScrollToEndChatLog) {
      if (logsRef && logsRef.current) {
        logsRef.current.scrollTo(0, logsRef.current.scrollHeight);
      }
      dispatch(setScrollToEndChatLog(false));
    }
  }, [scrollToEndChatLog, prevScrollToEndChatLog, dispatch]);

  // show score overview when it's set to show in redux
  const scoreOverviewVisible = useSelector(scoreOverviewVisibleSelector);

  const nextUnit = useSelector(nextUnitSelector);
  const courseID = useSelector(courseIDSelector);

  const handleShowScoreModal = useCallback(() => {
    dispatch(setScoreOverviewVisible(true));
  }, [dispatch]);

  const handleCloseScoreModal = useCallback(() => {
    dispatch(setScoreOverviewVisible(false));
  }, [dispatch]);

  const handleFeedbackTextShowEnd = useCallback(
    (itemIndex) => {
      if (chatState === ChatStates.TALK && itemIndex === chats.length - 1) {
        logsRef.current.scrollTo({
          top: logsRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    },
    [chatState, chats]
  );

  const showableScoreOverview = useSelector(showableScoreOverviewSelector);

  return (
    <Styled.LogsList ref={logsRef}>
      {chats.map((item, index) => (
        <ChatLogItem
          onFeedbackTextShowEnd={handleFeedbackTextShowEnd}
          key={index}
          index={index}
          logItem={item}
        />
      ))}
      {chatLoadingStatus === 'pending' && (
        <Styled.LoadingIndicatorWrapper>
          <Styled.LoadingIndicator>
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </Styled.LoadingIndicator>
        </Styled.LoadingIndicatorWrapper>
      )}
      {[ChatStates.COMPLETE].includes(chatState) && (
        <>
          <Styled.FinishWrapper>
            {showableScoreOverview && (
              <IconLabelButton onClick={handleShowScoreModal}>
                Show Score Summary
              </IconLabelButton>
            )}
            {!nextUnit && <FinishSessionButton courseID={courseID} />}
          </Styled.FinishWrapper>
          {nextUnit && (
            <UnitCompleteNext nextUnit={nextUnit} courseID={courseID} />
          )}
        </>
      )}
    </Styled.LogsList>
  );
};

VHLogsArea.propTypes = {};

export default VHLogsArea;
